import { InteroperabilityTabKey } from './tabs';

/**
 * Estado do módulo carregado na URL (MV2-1706).
 *
 * Por querystring e não por sub-rota: o AppRouter casa `permission.route` com o
 * `path` do staticRoutes por igualdade literal, então `/interoperability/:tab`
 * deixaria de casar com a permissão `/interoperability` e a página inteira
 * desapareceria. Query param mantém uma rota, uma permissão, e ainda assim dá
 * link direto para aba, parceiro e ação.
 */

export type InteroperabilityAction = 'invite' | 'accept';

export type InteroperabilityUrlState = {
  tab: InteroperabilityTabKey;

  /** Parceiro em foco: linha expandida em Conexões, ligação isolada no Mapa. */
  partner: string | null;

  /**
   * De quem se espera o token, quando `action` é 'accept'.
   *
   * Separado de `partner` de propósito. Os dois guardam um tenant, mas dizem coisas
   * diferentes: `partner` é foco de leitura e abre o painel de detalhe da conexão,
   * `from` é o convite que se vai revisar. Enquanto o aceite reusava `partner`,
   * "Revisar convite" abria o fluxo de aceite E o painel do parceiro ao mesmo tempo —
   * o painel vive num portal no body, então aparecia por cima mesmo com a aba
   * Conexões escondida atrás de outra (MV2-1823).
   */
  from: string | null;

  /** Modal aberto por link. */
  action: InteroperabilityAction | null;
};

const TABS: InteroperabilityTabKey[] = [
  'invites',
  'connections',
  'stations',
  'transactions',
  'audit',
  'api',
  'eligibility',
];

const ACTIONS: InteroperabilityAction[] = ['invite', 'accept'];

/**
 * Convites e a primeira aba, e o padrao acompanha: abrir numa aba que nao e a primeira faz
 * a barra parecer fora de ordem.
 *
 * Link antigo com ?tab=map ou ?tab=history cai aqui, e nao em tela branca — o mapa virou
 * uma visao dentro de Conexoes e o historico virou Auditoria.
 */
export const DEFAULT_TAB: InteroperabilityTabKey = 'invites';

/**
 * Lê o estado da querystring.
 *
 * Valor desconhecido cai no padrão em vez de quebrar: URL é entrada de fora —
 * chega editada à mão, truncada por cliente de e-mail ou de uma versão anterior
 * da tela.
 */
export const parseUrlState = (search: string): InteroperabilityUrlState => {
  const params = new URLSearchParams(search || '');

  const tab = params.get('tab') as InteroperabilityTabKey | null;
  const action = params.get('action') as InteroperabilityAction | null;
  const partner = params.get('partner');
  const from = params.get('from');

  return {
    tab: tab && TABS.includes(tab) ? tab : DEFAULT_TAB,
    partner: partner || null,
    from: from || null,
    action: action && ACTIONS.includes(action) ? action : null,
  };
};

/**
 * Monta a querystring do estado.
 *
 * A aba padrão e os campos vazios ficam fora: `/interoperability` limpo é o
 * endereço do módulo, e poluir com `?tab=connections` faria dois endereços para
 * a mesma tela.
 */
export const buildSearch = (state: Partial<InteroperabilityUrlState>): string => {
  const params = new URLSearchParams();

  if (state.tab && state.tab !== DEFAULT_TAB) {
    params.set('tab', state.tab);
  }

  if (state.partner) {
    params.set('partner', state.partner);
  }

  if (state.from) {
    params.set('from', state.from);
  }

  if (state.action) {
    params.set('action', state.action);
  }

  const search = params.toString();

  return search ? `?${search}` : '';
};

/** Se duas querystrings representam o mesmo estado, para não empilhar histórico. */
export const sameState = (a: string, b: string): boolean =>
  buildSearch(parseUrlState(a)) === buildSearch(parseUrlState(b));
